<template>
    <div class="app-publication-create">
        <slot></slot>

        <!-- publishing options 
        <app-button
            v-if="!isCreating"
            class="ml-2"
            @click="toggleProjectConfiguration()">
            {{
                showProjectConfiguration
                    ? "Hide Publish Settings"
                    : "Show Publish Settings"
            }}
        </app-button>-->
        <!-- create new publication button -->
        <app-button
            v-if="!isCreating"
            theme="primary"
            @click="isCreating = true">
            Create New Course
        </app-button>

        <div v-show="showProjectConfiguration" class="page-main__section">
            <!-- publication builder component -->
            <div class="page-main__section-header">
                <h3 class="page-main__section-title">Project Config Settings</h3>
                <p class="element-instructions">Define config settings, such as setting up Issue Tracker, that will be applied to all courses in this project.</p>
            </div>
            <code-editor
                ref="project-configuration"
                :value="getJSONString(projectConfiguration)"
                :placeholder="'{\n\t&quot;key&quot;: &quot;value&quot;\n}'"
                language="json"
                rows="10"
                @change="updateProjectConfiguration($event)"
                class="d-block w-100">
            </code-editor>
        </div>

        <!-- publication builder component -->
        <div v-if="isCreating">
            <publishing-edit-form
                :project-id="project.id"
                :driver="project.driver"
                :disabled="isSaving['new-publication']"
                :isClosed="false"
                :isCreate="true"
                @close="isCreating = false"
                @save="save">
            </publishing-edit-form>
            <app-error
                v-if="errors['new-publication']"
                v-bind="errors['new-publication']"
            />
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import Echo from '../helpers/echo'
import api from '../api/publisher'
import PublishingEditForm from './PublishingEditForm'

export default {
    components: { PublishingEditForm },
    props: {
        project: {
            type: Object,
        },
        items: {
            type: Object,
            default: () => Object({ data: [] }),
        },
    },
    data() {
        return {
            itemsPerPage: 20,
            isExporting: false,
            isCreating: true,
            isConfiguring: { },
            isEditing: { },
            isSaving: { },
            isPublishing: { },
            isPublishAndDownload: false,
            showProjectConfiguration: false,
            projectConfiguration: this.project.configuration,
            errors: { },
            publishingStatus: { },
            publications: this.items.data
        }
    },
    computed: {
        isAdmin() {
            return this.$store.getters.isAdmin
        },
        sortedPublications() {
            // return _.reverse(_.sortBy(this.publications, 'created_at'))
            return _.sortBy(this.publications, 'name')
        }
    },
    methods: {
        setSaving(id, value = true) {
            if (value) {
                this.clearErrors(id)
            }
            this.isSaving[id]= value
        },
        setPublishing(id, value = true) {
            this.isPublishing[id]= value
        },
        setConfiguring(id, value = true) {
            this.isConfiguring[id]= value
        },
        setEditing(id, value = true) {
            this.isEditing[id] = value
        },
        setErrors(id, value) {
            this.errors[id] = value
        },
        toggleProjectConfiguration(value) {
            if (_.isBoolean(value)) {
                this.showProjectConfiguration = value
            } else {
                this.showProjectConfiguration = !this.showProjectConfiguration
            }
        },
        clearErrors(id) {
            this.setErrors(id, null)
        },
        formatTime(ts) {
            return moment(ts).format('LLL')
        },
        getJSONString(obj) {
            if (!obj) {
                return ''
            }
            return JSON.stringify(obj, null, 2)
        },
        setupEchoListener(publication) {
            // subscribe to channel for each course
            Echo.private(`App.Models.Publication.${publication.id}`).notification(
                (notification) => {
                    this.publishingStatus[publication.id] = notification
                    const model = _.get(notification, 'model', { })
                    const index = _.findIndex(this.publications, { id: model.id })
                    this.publications[index] = Object.assign(this.publications[index], model)

                    if (notification.progress === 1 && model.id) {
                        this.setPublishing(model.id, false)
                        this.publishingStatus[model.id] = false
                    }
                }
            )
        },
        async updateProjectConfiguration(event) {
            let configuration = event.target.value
            if (_.isString(configuration) && !configuration.length) {
                configuration = null
            } else {
                configuration = JSON.parse(configuration)
            }

            // api call to save project configuration
            const result = await api.updateProjectConfiguration(
                this.project.id,
                configuration
            )

            if (result.errors) {
                // catch errors
                console.error(result.errors)
            } else {
                // update publication
                this.project.configuration = result.configuration
            }
        },
        async updateConfiguration(id, configuration) {
            if (_.isString(configuration) && !configuration.length) {
                configuration = null
            } else {
                configuration = JSON.parse(configuration)
            }

            // api call to save configuration
            await this.update({ id, configuration })
        },
        async save(publication) {
            this.setSaving('new-publication')

            // api call to save
            const result = await api.create(this.project.id, publication)

            if (result.errors) {
                // catch errors
                this.setErrors('new-publication', result)
            } else {
                // append  new publication to list
                this.publications.push(result)

                this.setupEchoListener(result)

                // clear temp publication
                this.isCreating = false
                this.setEditing(result.id, false)
            }

            this.setSaving('new-publication', false)
            window.location.href = '/projects/'+this.project.id+'/publications';
        },
        async update(publication) {
            
            this.setSaving(publication.id)

            // api call to save
            const result = await api.update(publication.id, publication)
            // console.log(result)

            if (result.errors) {
                // catch errors
                this.setErrors(publication.id, result)
            } else {
                // update publication
                const index = _.findIndex(this.publications, { id: result.id })
                this.publications[index] = Object.assign(this.publications[index], result)
            }

            this.setSaving(publication.id, false)
        },
        async remove(publication) {
            if (!confirm('Are you sure you want to delete this publication?')) {
                return
            }

            // remove publication locally
            const index = _.findIndex(this.publications, { id: publication.id })
            this.publications.splice(index, 1)

            // api call to save
            const result = await api.destroy(publication.id)

            if (result.errors) {
                // catch errors
                this.setErrors(publication.id, result)
                // put it back
                this.publications.splice(index, 0, publication)
            }
        },
        async publish(publication) {
            this.setPublishing(publication.id)

            // api call to process
            const result = await api.publish(publication.id)
            // console.log(result)

            if (result.errors) {
                // catch errors
                this.setErrors(publication.id, result)
            } else {
                // process publication
                const index = _.findIndex(this.publications, { id: result.id })
                this.publications[index] = Object.assign(this.publications[index], result)
            }
        },
        async bulkExport() {
            this.isExporting = true
            const result = await api.bulkExport(this.project.id)
            console.log(result)
            this.isExporting = false
        },
        async bulkPackageDownload() {
            this.isPublishAndDownload = true
            const result = await api.bulkPackage(this.project.id)
            console.log(result)
            this.isPublishAndDownload = false
        },
        async createLink (id) {
            const result = await api.createPublicLink(id)
            console.log(result)
            if (result.errors) {
                // catch errors
                console.error(result.errors)
            } else {
                this.updatePublication(id, { links: [result] })
            }
        },
        async resetLink(link) {
            if (!confirm('Are you sure you want to reset this link\'s expiration?')) {
                return
            }

            const result = await api.resetPublicLink(link)
            if (result.errors) {
                // catch errors
                console.error(result.errors)
            } else {
                this.updatePublication(result.publication_id, { links: [result] })
            }
        },
        async removeLink(link) {
            if (!confirm('Are you sure you want to delete this link?')) {
                return
            }

            const result = await api.deletePublicLink(link)
            if (result.errors) {
                // catch errors
                console.error(result.errors)
            } else {
                this.updatePublication(result.publication_id, { links: [] })
            }
        },
        async copyLink(url) {
            await navigator.clipboard.writeText(url)
        },
        updatePublication (id, data) {
            const index = _.findIndex(this.publications, { id })
            if (index >= 0) {
                const result = Object.assign({ }, this.publications[index], data)
                this.publications[index] = result
            }
        }
    },
    created() {
        const status = { }
        this.publications.forEach((publication, index) => {
            // set initial status
            status[publication.id] = false
            this.setupEchoListener(publication)
        })
        this.publishingStatus = status

        this.$store.commit('builder/setSourceType', 'project')

        this.$store.commit('builder/setSource', this.project)

        this.$store.commit(
            'builder/setDataObjectSource',
            this.project.data_objects
        )
        this.$store.commit(
            'builder/setDataObjects',
            _.cloneDeep(this.project.data_objects)
        )

        this.$store.commit('builder/setProject', this.project)
        this.$store.commit('builder/setDatasets', this.project.data_objects)
    }
}
</script>
